import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Building2, Rocket, Info, AlertCircle, CheckCircle } from 'lucide-react';
import {
  validateEmail, validatePassword, validateName, validateCIF,
  validateDNI, validateNIE, validateDocument,
  EU_COUNTRIES
} from '../lib/validations';

// Tipos de documento disponibles
const DOCUMENT_TYPES = [
  { value: 'CIF', label: 'CIF / NIF (Empresa)', placeholder: 'B12345678' },
  { value: 'DNI', label: 'DNI', placeholder: '12345678Z' },
  { value: 'NIE', label: 'NIE / TIE', placeholder: 'X1234567L' },
  { value: 'PASAPORTE', label: 'Pasaporte', placeholder: 'AAA123456' },
  { value: 'OTRO', label: 'Otro documento', placeholder: 'Número de documento' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, registerCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    nombre: '',
    tenant_name: '',
    tenant_doc_type: 'CIF',
    tenant_cif: '',
    country: 'ES'
  });

  // Validation errors
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validate a single field
  const validateField = (name, value) => {
    let result = { valid: true };

    switch (name) {
      case 'email':
      case 'register-email':
        result = validateEmail(value);
        break;
      case 'password':
      case 'register-password':
        result = validatePassword(value);
        break;
      case 'nombre':
        result = validateName(value, { minChars: 3 });
        break;
      case 'tenant_name':
        result = validateName(value, { minChars: 3 });
        break;
      case 'tenant_cif':
        if (value) {
          // Validar según el tipo de documento seleccionado
          result = validateDocument(value, registerData.tenant_doc_type, { required: false });
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: result.valid ? null : result.error
    }));

    return result.valid;
  };

  // Handle document type change - clear validation and value
  const handleDocTypeChange = (newType) => {
    setRegisterData(prev => ({
      ...prev,
      tenant_doc_type: newType,
      tenant_cif: '' // Limpiar el valor al cambiar tipo
    }));
    setErrors(prev => ({ ...prev, tenant_cif: null }));
    setTouched(prev => ({ ...prev, tenant_cif: false }));
  };

  // Get current document type config
  const getCurrentDocType = () => {
    return DOCUMENT_TYPES.find(t => t.value === registerData.tenant_doc_type) || DOCUMENT_TYPES[0];
  };

  // Handle field blur
  const handleBlur = (name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  // Validate entire form
  const validateLoginForm = () => {
    const emailValid = validateField('email', loginData.email);
    return emailValid && loginData.password;
  };

  const validateRegisterForm = () => {
    const emailValid = validateField('register-email', registerData.email);
    const passwordValid = validateField('register-password', registerData.password);
    const nameValid = validateField('nombre', registerData.nombre);
    const companyValid = validateField('tenant_name', registerData.tenant_name);
    const cifValid = registerData.tenant_cif ? validateField('tenant_cif', registerData.tenant_cif) : true;

    return emailValid && passwordValid && nameValid && companyValid && cifValid;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!validateLoginForm()) {
      toast.error('Por favor, corrige los errores del formulario');
      return;
    }

    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('¡Bienvenido de nuevo!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterCompany = async (e) => {
    e.preventDefault();

    if (!validateRegisterForm()) {
      toast.error('Por favor, corrige los errores del formulario');
      return;
    }

    setLoading(true);
    try {
      const result = await registerCompany({
        email: registerData.email,
        password: registerData.password,
        nombre: registerData.nombre,
        tenant_name: registerData.tenant_name,
        tenant_cif: registerData.tenant_cif,
        country: registerData.country
      });
      toast.success(result.message || '¡Empresa registrada exitosamente!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar empresa');
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = () => {
    setLoginData({ email: 'admin@leapifyes.com', password: 'Adminmando' });
    setErrors({});
  };

  // Helper to render input with validation state
  const renderValidationIcon = (fieldName, value) => {
    if (!touched[fieldName]) return null;
    if (errors[fieldName]) {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    if (value) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return null;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Panel - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1760331840595-72c9681e7e71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwcmVub3ZhdGlvbnxlbnwwfHx8fDE3Njc3NzcxNTJ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_reforma-manager/artifacts/gaylguq8_MANDO%20%C2%B7%20by%20Leapifyes.png"
              alt="MANDO Logo"
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight">MANDO</span>
              <span className="text-xs text-slate-300 tracking-wide">by Leapifyes</span>
            </div>
          </div>

          <div className="space-y-6">
            <h1 className="text-4xl font-bold leading-tight">
              Tu empresa.<br />
              Tu sistema.<br />
              Tu control.
            </h1>
            <p className="text-slate-300 max-w-md">
              El sistema de gestión inteligente diseñado para empresas de reformas y construcción que quieren crecer con control total.
            </p>
          </div>

          <div className="flex gap-6 text-sm text-slate-300">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span>Multi-tenant</span>
            </div>
            <div className="flex items-center gap-2">
              <Rocket className="w-4 h-4" />
              <span>White-label</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span>Aislamiento total</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_reforma-manager/artifacts/gaylguq8_MANDO%20%C2%B7%20by%20Leapifyes.png"
              alt="MANDO Logo"
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight text-slate-900">MANDO</span>
              <span className="text-xs text-slate-500 tracking-wide">by Leapifyes</span>
            </div>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center">Acceder al sistema</CardTitle>
              <CardDescription className="text-center">
                Gestiona tu negocio de reformas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Registrar Empresa</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="login-email" className={errors.email && touched.email ? 'text-red-600' : ''}>
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="login-email"
                          name="email"
                          type="email"
                          placeholder="tu@empresa.es"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          onBlur={() => handleBlur('email', loginData.email)}
                          required
                          data-testid="login-email-input"
                          className={`pr-10 ${errors.email && touched.email ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {renderValidationIcon('email', loginData.email)}
                        </div>
                      </div>
                      {errors.email && touched.email && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Contraseña</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                        data-testid="login-password-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Cargando...' : 'Iniciar Sesión'}
                    </Button>
                  </form>

                  {/* Demo Credentials Banner */}
                  <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">Acceso Demo</p>
                        <p className="text-amber-700 text-xs mt-1">
                          <strong>Email:</strong> admin@leapifyes.com<br />
                          <strong>Contraseña:</strong> Adminmando
                        </p>
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="p-0 h-auto text-amber-700 hover:text-amber-900 mt-1"
                          onClick={fillDemoCredentials}
                          data-testid="fill-demo-btn"
                        >
                          Usar credenciales demo →
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegisterCompany} className="space-y-4">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <p className="text-xs text-blue-700">
                        <strong>Registra tu empresa</strong> y obtén 7 días de prueba gratuita con acceso completo.
                      </p>
                    </div>

                    {/* Company Section */}
                    <div className="space-y-1.5">
                      <Label htmlFor="register-company" className={errors.tenant_name && touched.tenant_name ? 'text-red-600' : ''}>
                        Nombre de la Empresa <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="register-company"
                          placeholder="Reformas García S.L."
                          value={registerData.tenant_name}
                          onChange={(e) => setRegisterData({ ...registerData, tenant_name: e.target.value })}
                          onBlur={() => handleBlur('tenant_name', registerData.tenant_name)}
                          required
                          data-testid="register-company-input"
                          className={`pr-10 ${errors.tenant_name && touched.tenant_name ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {renderValidationIcon('tenant_name', registerData.tenant_name)}
                        </div>
                      </div>
                      {errors.tenant_name && touched.tenant_name && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.tenant_name}
                        </p>
                      )}
                    </div>

                    {/* Document Type and Number */}
                    <div className="space-y-2">
                      <Label className={errors.tenant_cif && touched.tenant_cif ? 'text-red-600' : ''}>
                        Documento fiscal (opcional)
                      </Label>
                      <div className="grid grid-cols-5 gap-2">
                        <div className="col-span-2">
                          <Select
                            value={registerData.tenant_doc_type}
                            onValueChange={handleDocTypeChange}
                          >
                            <SelectTrigger data-testid="register-doc-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {DOCUMENT_TYPES.map((docType) => (
                                <SelectItem key={docType.value} value={docType.value}>
                                  {docType.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-3 relative">
                          <Input
                            id="register-cif"
                            placeholder={getCurrentDocType().placeholder}
                            value={registerData.tenant_cif}
                            onChange={(e) => setRegisterData({ ...registerData, tenant_cif: e.target.value.toUpperCase() })}
                            onBlur={() => handleBlur('tenant_cif', registerData.tenant_cif)}
                            data-testid="register-cif-input"
                            className={`pr-10 ${errors.tenant_cif && touched.tenant_cif ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {renderValidationIcon('tenant_cif', registerData.tenant_cif)}
                          </div>
                        </div>
                      </div>
                      {errors.tenant_cif && touched.tenant_cif && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.tenant_cif}
                        </p>
                      )}
                      <p className="text-xs text-slate-500">
                        {registerData.tenant_doc_type === 'CIF' && 'Formato: Letra + 7 dígitos + control (ej: B12345678)'}
                        {registerData.tenant_doc_type === 'DNI' && 'Formato: 8 dígitos + letra (ej: 12345678Z)'}
                        {registerData.tenant_doc_type === 'NIE' && 'Formato: X/Y/Z + 7 dígitos + letra (ej: X1234567L)'}
                        {registerData.tenant_doc_type === 'PASAPORTE' && 'Introduce el número de pasaporte'}
                        {registerData.tenant_doc_type === 'OTRO' && 'Introduce el número del documento'}
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="register-country">País <span className="text-red-500">*</span></Label>
                      <Select
                        value={registerData.country}
                        onValueChange={(value) => setRegisterData({ ...registerData, country: value })}
                      >
                        <SelectTrigger id="register-country" data-testid="register-country-select">
                          <SelectValue placeholder="Selecciona país" />
                        </SelectTrigger>
                        <SelectContent>
                          {EU_COUNTRIES.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <p className="text-xs text-slate-500 mb-3">Datos del administrador</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="register-name" className={errors.nombre && touched.nombre ? 'text-red-600' : ''}>
                        Tu Nombre <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="register-name"
                          placeholder="Juan García López"
                          value={registerData.nombre}
                          onChange={(e) => setRegisterData({ ...registerData, nombre: e.target.value })}
                          onBlur={() => handleBlur('nombre', registerData.nombre)}
                          required
                          data-testid="register-name-input"
                          className={`pr-10 ${errors.nombre && touched.nombre ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {renderValidationIcon('nombre', registerData.nombre)}
                        </div>
                      </div>
                      {errors.nombre && touched.nombre && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.nombre}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="register-email" className={errors['register-email'] && touched['register-email'] ? 'text-red-600' : ''}>
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="tu@empresa.es"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          onBlur={() => handleBlur('register-email', registerData.email)}
                          required
                          data-testid="register-email-input"
                          className={`pr-10 ${errors['register-email'] && touched['register-email'] ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {renderValidationIcon('register-email', registerData.email)}
                        </div>
                      </div>
                      {errors['register-email'] && touched['register-email'] && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors['register-email']}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="register-password" className={errors['register-password'] && touched['register-password'] ? 'text-red-600' : ''}>
                        Contraseña <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="Mín. 8 caracteres, mayúscula, número"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          onBlur={() => handleBlur('register-password', registerData.password)}
                          required
                          data-testid="register-password-input"
                          className={`pr-10 ${errors['register-password'] && touched['register-password'] ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {renderValidationIcon('register-password', registerData.password)}
                        </div>
                      </div>
                      {errors['register-password'] && touched['register-password'] && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors['register-password']}
                        </p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creando empresa...' : 'Registrar Mi Empresa'}
                    </Button>

                    <p className="text-xs text-center text-slate-500 mt-2">
                      Al registrarte aceptas los Términos y la Política de Privacidad
                    </p>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-center text-xs text-slate-400 mt-6">
            MANDO Smart Control™ · by Leapifyes
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;